/**
 * Scene-memory budget policy (Graphics G5).
 *
 * The effective VRAM budget is the quality tier's budget CLAMPED by the world's
 * server-authoritative Land-Impact cap (`worldBuildBudget.liMax`) — a heavy world can't
 * exceed the device limit. `disposalAudit` snapshots the renderer's GPU-resource counts
 * (the leak/baseline metric — counts, not bytes, since WebGPU has no public byte counter
 * and GPUMemoryTracker needs the optional webgpu-memory dep on a real WebGPU backend).
 */
import type * as THREE from 'three/webgpu';

/** Rough VRAM (MB) a unit of Land-Impact may pull in — maps the server LI cap to a client memory cap. */
export const LI_VRAM_MB = 0.5;

/** The effective scene-memory budget: the tier budget, clamped down by the world's LI cap. */
export function effectiveBudgetMb(tierBudgetMb: number, worldLiMax: number): number {
  if (worldLiMax <= 0) return tierBudgetMb; // no world cap → the tier budget
  return Math.min(tierBudgetMb, Math.max(64, worldLiMax * LI_VRAM_MB));
}

export interface MemoryAudit { geometries: number; textures: number; }

/** Snapshot the renderer's GPU-resource counts (the leak/baseline metric). */
export function disposalAudit(renderer: THREE.WebGPURenderer): MemoryAudit {
  const m = (renderer as unknown as { info: { memory: { geometries?: number; textures?: number } } }).info.memory;
  return { geometries: m.geometries ?? 0, textures: m.textures ?? 0 };
}
